import React, { useState } from 'react';
import './Form.css';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

// URL вашего Google Apps Script веб-приложения
// Замените на ваш URL после настройки скрипта
const GOOGLE_SCRIPT_URL = process.env.REACT_APP_GOOGLE_SCRIPT_URL || '';

const Form = () => {
  const { language } = useLanguage();
  const t = translations[language];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Сбрасываем статус при изменении формы
    if (submitStatus) {
      setSubmitStatus(null);
      setSubmitMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Валидация
    if (!formData.name || !formData.email) {
      setSubmitStatus('error');
      setSubmitMessage(language === 'ru' ? 'Пожалуйста, заполните обязательные поля' : 'Міндетті өрістерді толтырыңыз');
      return;
    }

    if (!GOOGLE_SCRIPT_URL) {
      setSubmitStatus('error');
      setSubmitMessage(language === 'ru' 
        ? 'Ошибка конфигурации: не указан URL скрипта' 
        : 'Конфигурация қатесі: скрипт URL көрсетілмеген');
      console.error('Google Script URL не настроен. Установите REACT_APP_GOOGLE_SCRIPT_URL в .env файле');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      // Отправляем данные в Google Sheets через Apps Script
      // Используем два метода для надежности
      const payload = {
        name: formData.name,
        email: formData.email,
        organization: formData.organization || '',
        message: formData.message || '',
        timestamp: new Date().toISOString(),
        language: language
      };

      console.log('=== ОТПРАВКА ФОРМЫ ===');
      console.log('Данные:', payload);
      console.log('URL скрипта:', GOOGLE_SCRIPT_URL);
      console.log('URL пустой?', !GOOGLE_SCRIPT_URL);

      // Метод 1: Отправка через URL параметры (GET) - самый надежный для Google Apps Script
      // Преобразуем данные в URL параметры
      const urlParams = new URLSearchParams();
      urlParams.append('name', payload.name);
      urlParams.append('email', payload.email);
      urlParams.append('organization', payload.organization || '');
      urlParams.append('message', payload.message || '');
      urlParams.append('timestamp', payload.timestamp);
      urlParams.append('language', payload.language);
      
      const urlWithParams = GOOGLE_SCRIPT_URL + '?' + urlParams.toString();
      console.log('Отправляем через URL параметры:', urlWithParams);
      
      // Используем скрытое изображение для отправки GET запроса (надежный метод)
      const img = document.createElement('img');
      img.src = urlWithParams;
      img.style.display = 'none';
      img.onload = () => {
        console.log('Данные отправлены через GET запрос');
      };
      img.onerror = (err) => {
        console.warn('Ошибка при отправке через GET:', err);
      };
      document.body.appendChild(img);
      
      // Удаляем изображение через 2 секунды
      setTimeout(() => {
        if (img.parentNode) {
          document.body.removeChild(img);
        }
      }, 2000);
      
      // Показываем успех
      setSubmitStatus('success');
      setSubmitMessage(language === 'ru' 
        ? 'Спасибо! Ваша заявка отправлена.' 
        : 'Рахмет! Сіздің өтінішіңіз жіберілді.');
      
      // Очищаем форму
      setFormData({
        name: '',
        email: '',
        organization: '',
        message: ''
      });

      // Скрываем сообщение через 5 секунд
      setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 5000);
      
      return;

    } catch (error) {
      console.error('Ошибка при отправке формы:', error);
      setSubmitStatus('error');
      setSubmitMessage(language === 'ru' 
        ? `Произошла ошибка: ${error.message}. Проверьте консоль браузера и настройки скрипта.` 
        : `Қате орын алды: ${error.message}. Браузер консольін және скрипт параметрлерін тексеріңіз.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="form-section" id="contact-form">
      <div className="form-container">
        <div className="form-card">
          <div className="form-title">
            <h2 className="form-heading">{t.formTitle}</h2>
            <p className="form-subtitle">
              {t.formSubtitle}
            </p>
          </div>
          
          <form className="form-inputs" onSubmit={handleSubmit}>
            <div className="form-inputs-row">
              <div className="form-input">
                <label className="form-label">{t.formName}</label>
                <input
                  type="text"
                  name="name"
                  className="form-field"
                  placeholder={t.formNamePlaceholder}
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-input">
                <label className="form-label">{t.formEmail}</label>
                <input
                  type="email"
                  name="email"
                  className="form-field"
                  placeholder={t.formEmailPlaceholder}
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            <div className="form-input">
              <label className="form-label">{t.formOrganization}</label>
              <input
                type="text"
                name="organization"
                className="form-field"
                placeholder={t.formOrganizationPlaceholder}
                value={formData.organization}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-input">
              <label className="form-label">{t.formMessage}</label>
              <textarea
                name="message"
                className="form-field form-textarea"
                placeholder={t.formMessagePlaceholder}
                value={formData.message}
                onChange={handleChange}
                rows="4"
              />
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              <button 
                type="submit" 
                className="form-button"
                disabled={isSubmitting}
                style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
              >
                {isSubmitting 
                  ? (language === 'ru' ? 'Отправка...' : 'Жіберілуде...') 
                  : t.formButton}
              </button>
              
              {submitStatus && (
                <div 
                  style={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    backgroundColor: submitStatus === 'success' ? '#d4edda' : '#f8d7da',
                    color: submitStatus === 'success' ? '#155724' : '#721c24',
                    border: `1px solid ${submitStatus === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                    transition: 'all 0.3s ease'
                  }}
                >
                  {submitMessage}
                </div>
              )}
            </div>
          </form>
        </div>
        
        <div className="form-banner" style={{ backgroundImage: 'url(/images/form-banner-bg.jpg)' }}>
          <div className="form-banner-overlay"></div>
          <div className="form-banner-logo">
            <span className="form-logo-shai">shai.</span>
            <span className="form-logo-academy">academy</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Form;

